import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';

import type { DataTransfer, DateRange } from '@cast/shared-types';
import { DataTransferCost, WorkloadDataTransferCostItem } from '@cast/types';
import {
  EMPTY_DATA_ITEM,
  aggregatorByTimestampFn,
  sumDataTransferCost,
} from '@cast/utils';

import { TimeSeries } from 'types/metrics';
import { TopMostExpensiveGroupDatapoint } from 'types/top-items';
import { compensateDST } from 'utils/compensateDST';

import { DataTransferTimeSeries } from '../types/networkCostOverTime';

export const getNetworkCostPrecision = (v?: number | null): number => {
  return v && v < 1 ? 6 : 2;
};

export const normalizeData = (
  [from, to]: DateRange,
  items?: DataTransferCost[]
): DataTransferCost[] => {
  return (
    items?.map((item) => ({
      ...item,
      ingressMetrics: item.ingressMetrics
        ? compensateDST([from, to], item.ingressMetrics)
        : undefined,
      egressMetrics: item.egressMetrics
        ? compensateDST([from, to], item.egressMetrics)
        : undefined,
    })) || []
  );
};

export const dataTransferDatapointAggregator = (
  name: string,
  acc: Record<string, TopMostExpensiveGroupDatapoint[number]>,
  metrics: WorkloadDataTransferCostItem
): void => {
  if (metrics.timestamp) {
    acc[metrics.timestamp] ??= {
      timestamp: metrics.timestamp,
      values: {},
    };

    let v = acc[metrics.timestamp].values[name];
    const sum = sumDataTransferCost(metrics);
    v = (v ?? 0) + sum;
    acc[metrics.timestamp].values[name] = v;
  }
};

export const aggregateDataTransferDatapoints = (
  data?: DataTransferCost[]
): DataTransferTimeSeries => {
  const dataByTimestamp = data?.reduce<Record<string, DataTransfer>>(
    (acc, current) => {
      current.egressMetrics?.forEach(
        aggregatorByTimestampFn.bind(null, acc, 'egress')
      );
      current.ingressMetrics?.forEach(
        aggregatorByTimestampFn.bind(null, acc, 'ingress')
      );
      return acc;
    },
    {}
  );
  return orderBy(
    Object.keys(dataByTimestamp ?? {}).reduce((acc, timestamp) => {
      acc.push({
        timestamp: dayjs.utc(timestamp).subtract(1, 'day').toISOString(),
        ...dataByTimestamp![timestamp],
      });
      return acc;
    }, [] as DataTransferTimeSeries),
    'timestamp'
  );
};

export const convertChartDatapointToCostReportData = (
  data: DataTransferTimeSeries | TimeSeries<object>
): DataTransferTimeSeries => {
  const today = dayjs.utc();

  return data
    .map((d) => {
      return {
        timestamp: d.timestamp,
        egress: 'egress' in d ? d.egress : cloneDeep(EMPTY_DATA_ITEM),
        ingress: 'ingress' in d ? d.ingress : cloneDeep(EMPTY_DATA_ITEM),
      };
    })
    .filter(({ timestamp }) => dayjs.utc(timestamp).isSameOrBefore(today));
};
